import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SEO from './SEO';
import './PetDetail.css';

const toNumber = (value) => {
    if (value === null || value === undefined) return null;
    const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : null;
};

const formatMoney = (value) => {
    const n = toNumber(value);
    if (n === null) return null;
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const isEstimate = (bill) => String(bill.documentType || '').toLowerCase() === 'estimate';

// What the owner actually paid for a service line. Prefer amountPaid (set when
// a discount/promotion reduced the charge) so "Total spent" reflects real
// spending, not the hospital's list price used for comparison.
const serviceSpend = (service) => {
    const paid = toNumber(service.amountPaid) ?? toNumber(service.normalized?.amountPaid);
    if (paid !== null && paid !== undefined) {
        return paid;
    }
    return toNumber(service.totalCost)
        ?? toNumber(service.normalized?.total)
        ?? toNumber(service.normalized?.actualCost)
        ?? toNumber(service.unitCost)
        ?? 0;
};

// Low/high totals for an estimate's service line.
const serviceEstimateRange = (service) => {
    const low = toNumber(service.lowCost) ?? toNumber(service.normalized?.lowEstimate);
    const high = toNumber(service.highCost) ?? toNumber(service.normalized?.highEstimate)
        ?? toNumber(service.totalCost) ?? toNumber(service.normalized?.total);
    return { low: low ?? high ?? 0, high: high ?? low ?? 0 };
};

const billSpend = (bill) => (bill.services || []).reduce((sum, s) => sum + serviceSpend(s), 0);

const billEstimateRange = (bill) => (bill.services || []).reduce((acc, s) => {
    const { low, high } = serviceEstimateRange(s);
    return { low: acc.low + low, high: acc.high + high };
}, { low: 0, high: 0 });

const serviceCategory = (service) => {
    return service.standardization?.category || service.category || 'Other';
};

const PetDetail = () => {
    const { petId } = useParams();
    const navigate = useNavigate();
    const [pet, setPet] = useState(null);
    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({ name: '', species: '', breed: '', birthYear: '' });
    const [editError, setEditError] = useState('');
    const [saving, setSaving] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState('');

    const handleGenerateSummary = async () => {
        setSummaryLoading(true);
        setSummaryError('');
        try {
            const response = await fetch(`/api/pets/${petId}/summary`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Unable to generate a summary right now.');
            }
            setPet(current => ({ ...current, aiSummary: data.aiSummary }));
        } catch (error) {
            setSummaryError(error.message);
        } finally {
            setSummaryLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        setLoadError('');
        fetch(`/api/pets/${petId}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
            .then(async response => {
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data.message || 'Unable to load this pet');
                }
                setPet(data.pet);
                setBills(Array.isArray(data.bills) ? data.bills : []);
            })
            .catch(error => setLoadError(error.message))
            .finally(() => setLoading(false));
    }, [petId]);

    const totals = useMemo(() => {
        const realBills = bills.filter(bill => !isEstimate(bill));
        const estimates = bills.filter(isEstimate);
        const totalSpent = realBills.reduce((sum, bill) => sum + billSpend(bill), 0);

        const byCategory = new Map();
        realBills.forEach(bill => {
            (bill.services || []).forEach(service => {
                const category = serviceCategory(service);
                byCategory.set(category, (byCategory.get(category) || 0) + serviceSpend(service));
            });
        });
        const categories = Array.from(byCategory.entries())
            .filter(([, amount]) => amount > 0)
            .sort((a, b) => b[1] - a[1]);

        const byYear = new Map();
        realBills.forEach(bill => {
            const year = new Date(bill.billDate).getFullYear();
            if (!Number.isFinite(year)) return;
            byYear.set(year, (byYear.get(year) || 0) + billSpend(bill));
        });
        const years = Array.from(byYear.entries()).sort((a, b) => b[0] - a[0]);
        const maxYearTotal = years.reduce((max, [, amount]) => Math.max(max, amount), 0);

        return { totalSpent, billCount: realBills.length, estimateCount: estimates.length, categories, years, maxYearTotal };
    }, [bills]);

    const startEdit = () => {
        setEditForm({
            name: pet.name || '',
            species: pet.species || '',
            breed: pet.breed || '',
            birthYear: pet.birthYear || ''
        });
        setEditError('');
        setEditing(true);
    };

    const handleEditSave = async (event) => {
        event.preventDefault();
        if (!editForm.name.trim()) {
            setEditError('Please give the pet a name.');
            return;
        }
        setSaving(true);
        setEditError('');
        try {
            const response = await fetch(`/api/pets/${petId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    ...editForm,
                    birthYear: editForm.birthYear ? Number(editForm.birthYear) : null
                })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Unable to update pet profile');
            }
            setPet(data.pet);
            setEditing(false);
        } catch (error) {
            setEditError(error.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="pet-detail"><div className="pet-detail-inner">Loading...</div></div>;
    }

    if (loadError || !pet) {
        return (
            <div className="pet-detail">
                <div className="pet-detail-inner">
                    <p className="pet-load-error">{loadError || 'Pet profile not found.'}</p>
                    <Link to="/dashboard">← Back to dashboard</Link>
                </div>
            </div>
        );
    }

    const petMeta = [pet.species, pet.breed, pet.birthYear ? `born ${pet.birthYear}` : null]
        .filter(Boolean).join(' · ');

    return (
        <div className="pet-detail">
            <SEO title={`${pet.name} — pet history`} />
            <div className="pet-detail-inner">
                <button className="back-button" onClick={() => navigate('/dashboard')}>← Back to dashboard</button>

                <div className="pet-detail-header">
                    <div>
                        <h2>{pet.name}</h2>
                        <p className="pet-meta">{petMeta || 'No details yet'}</p>
                        <p className="pet-privacy-note">Pet profiles and visit history are private — only you can see this page.</p>
                    </div>
                    {!editing && (
                        <button type="button" className="secondary-btn" onClick={startEdit}>Edit profile</button>
                    )}
                </div>

                {editing && (
                    <form className="pet-edit-form" onSubmit={handleEditSave}>
                        <label>
                            <span>Name</span>
                            <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                        </label>
                        <label>
                            <span>Species</span>
                            <input placeholder="dog, cat..." value={editForm.species} onChange={e => setEditForm(f => ({ ...f, species: e.target.value }))} />
                        </label>
                        <label>
                            <span>Breed</span>
                            <input value={editForm.breed} onChange={e => setEditForm(f => ({ ...f, breed: e.target.value }))} />
                        </label>
                        <label>
                            <span>Birth year</span>
                            <input type="number" min="1980" max="2100" value={editForm.birthYear} onChange={e => setEditForm(f => ({ ...f, birthYear: e.target.value }))} />
                        </label>
                        <div className="pet-edit-actions">
                            <button type="submit" className="next-btn" disabled={saving || !editForm.name.trim()}>{saving ? 'Saving...' : 'Save'}</button>
                            <button type="button" className="secondary-btn" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
                        </div>
                        {editError && <p className="pet-load-error">{editError}</p>}
                    </form>
                )}

                <div className="pet-summary-row">
                    <div className="pet-summary-tile">
                        <strong>Total spent</strong>
                        <span className="pet-summary-value">{formatMoney(totals.totalSpent) || '$0.00'}</span>
                        <span className="pet-summary-note">from {totals.billCount} bill{totals.billCount === 1 ? '' : 's'}</span>
                    </div>
                    <div className="pet-summary-tile">
                        <strong>Visits recorded</strong>
                        <span className="pet-summary-value">{bills.length}</span>
                        <span className="pet-summary-note">{totals.billCount} bill{totals.billCount === 1 ? '' : 's'}, {totals.estimateCount} estimate{totals.estimateCount === 1 ? '' : 's'}</span>
                    </div>
                </div>

                {totals.categories.length > 0 && (
                    <div className="pet-category-panel">
                        <h3>Spending by category</h3>
                        <ul>
                            {totals.categories.map(([category, amount]) => (
                                <li key={category}>
                                    <span>{category}</span>
                                    <strong>{formatMoney(amount)}</strong>
                                </li>
                            ))}
                        </ul>
                        <p className="pet-summary-note">Totals come from saved bills. Estimates appear in the timeline but are not counted as spending.</p>
                    </div>
                )}

                {totals.years.length > 1 && (
                    <div className="pet-category-panel">
                        <h3>Spending by year</h3>
                        <ul className="pet-year-list">
                            {totals.years.map(([year, amount]) => (
                                <li key={year}>
                                    <span className="pet-year-label">{year}</span>
                                    <span className="pet-year-bar-track">
                                        <span
                                            className="pet-year-bar"
                                            style={{ width: `${totals.maxYearTotal > 0 ? Math.max(4, Math.round((amount / totals.maxYearTotal) * 100)) : 0}%` }}
                                        />
                                    </span>
                                    <strong>{formatMoney(amount)}</strong>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {bills.length > 0 && (
                    <div className="pet-ai-panel">
                        <div className="pet-ai-heading">
                            <h3>Care cost summary</h3>
                            <button
                                type="button"
                                className="secondary-btn"
                                onClick={handleGenerateSummary}
                                disabled={summaryLoading}
                            >
                                {summaryLoading ? 'Summarizing...' : pet.aiSummary ? 'Refresh summary' : 'Generate summary'}
                            </button>
                        </div>

                        {summaryError && <p className="pet-load-error">{summaryError}</p>}

                        {pet.aiSummary ? (
                            <>
                                <p className="pet-ai-recap">{pet.aiSummary.recap}</p>
                                {Array.isArray(pet.aiSummary.recurring) && pet.aiSummary.recurring.length > 0 && (
                                    <div className="pet-ai-recurring">
                                        <h4>Recurring costs spotted</h4>
                                        <ul>
                                            {pet.aiSummary.recurring.map((item, index) => (
                                                <li key={index}>
                                                    <div className="pet-ai-recurring-top">
                                                        <span>{item.item}</span>
                                                        <strong>
                                                            {item.typicalCost != null ? formatMoney(item.typicalCost) : ''}
                                                            {item.cadence && item.cadence !== 'unclear' ? ` · ${item.cadence}` : ''}
                                                        </strong>
                                                    </div>
                                                    {item.note && <p>{item.note}</p>}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {pet.aiSummary.generatedAt && (
                                    <p className="pet-summary-note">Generated {new Date(pet.aiSummary.generatedAt).toLocaleDateString()} from {pet.aiSummary.sourceBillCount} upload{pet.aiSummary.sourceBillCount === 1 ? '' : 's'}.</p>
                                )}
                            </>
                        ) : (
                            !summaryLoading && !summaryError && (
                                <p className="pet-summary-note">Get a plain-language recap of {pet.name}'s visit history and any repeating costs (like monthly meds or annual vaccines).</p>
                            )
                        )}

                        <p className="pet-ai-disclaimer">Summaries describe costs and visit history from your uploads only. They are not veterinary or medical advice.</p>
                    </div>
                )}

                <h3 className="pet-timeline-heading">Visit timeline</h3>
                {bills.length === 0 ? (
                    <div className="pet-empty-card">
                        No uploads attached to {pet.name} yet. Attach a bill or estimate from your <Link to="/dashboard">dashboard</Link>, or <Link to="/upload/step1">upload a new one</Link>.
                    </div>
                ) : (
                    <ol className="pet-timeline">
                        {bills.map(bill => {
                            const estimate = isEstimate(bill);
                            const range = estimate ? billEstimateRange(bill) : null;
                            const spend = estimate ? null : billSpend(bill);
                            return (
                                <li key={bill._id} className="pet-visit">
                                    <div className="pet-visit-marker" aria-hidden="true" />
                                    <Link to={`/bills/${bill._id}`} className="pet-visit-card">
                                        <div className="pet-visit-top">
                                            <span className="pet-visit-date">{new Date(bill.billDate).toLocaleDateString()}</span>
                                            <span className={`pet-visit-type ${estimate ? 'is-estimate' : 'is-bill'}`}>{estimate ? 'Estimate' : 'Bill'}</span>
                                        </div>
                                        <strong className="pet-visit-hospital">{bill.vetHospital || 'Unknown hospital'}</strong>
                                        <ul className="pet-visit-services">
                                            {(bill.services || []).slice(0, 4).map((service, index) => (
                                                <li key={index}>
                                                    <span>{service.description}</span>
                                                    <span>
                                                        {estimate
                                                            ? (() => {
                                                                const r = serviceEstimateRange(service);
                                                                return r.high > 0 ? `${formatMoney(r.low)} – ${formatMoney(r.high)}` : '';
                                                            })()
                                                            : formatMoney(serviceSpend(service))}
                                                    </span>
                                                </li>
                                            ))}
                                            {(bill.services || []).length > 4 && (
                                                <li className="pet-visit-more">+ {(bill.services || []).length - 4} more service{(bill.services || []).length - 4 === 1 ? '' : 's'}</li>
                                            )}
                                        </ul>
                                        <div className="pet-visit-total">
                                            {estimate
                                                ? <span>Estimated: {formatMoney(range.low)} – {formatMoney(range.high)}</span>
                                                : <span>Visit total: {formatMoney(spend)}</span>}
                                        </div>
                                    </Link>
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>
        </div>
    );
};

export default PetDetail;
